import { useEffect, useState } from "react";
import { useT } from "../../i18n/I18nProvider";

// window.confirm() and window.alert() are blocked inside the
// marketplace iframe (sandbox attributes strip them), so any call
// silently no-ops and the operator never gets feedback. This module
// is the replacement: an async confirmDialog() / notifyDialog()
// pair backed by a single <ModalHost> mounted at the app root.
//
// The helpers are intentionally imperative (Promise-returning) so
// they slot into existing async handlers without forcing every
// caller to lift state into a useState hook.

type Variant = "info" | "danger";

interface DialogRequest {
  kind: "confirm" | "notify";
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: Variant;
  resolve: (ok: boolean) => void;
}

type Listener = (req: DialogRequest | null) => void;

let current: DialogRequest | null = null;
const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l(current);
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function confirmDialog(opts: {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: Variant;
}): Promise<boolean> {
  // Drop any in-flight dialog — the new one wins. Resolves the old
  // promise as a cancel so callers awaiting it don't hang.
  if (current) current.resolve(false);
  return new Promise<boolean>((resolve) => {
    current = { kind: "confirm", ...opts, resolve };
    emit();
  });
}

export function notifyDialog(opts: { title?: string; message: string }): Promise<void> {
  if (current) current.resolve(false);
  return new Promise<void>((resolve) => {
    current = {
      kind: "notify",
      ...opts,
      resolve: () => resolve(),
    };
    emit();
  });
}

export function ModalHost() {
  const t = useT();
  const [req, setReq] = useState<DialogRequest | null>(current);

  useEffect(() => subscribe(setReq), []);

  // ESC closes the dialog as a cancel (for confirm) or OK (for notify).
  useEffect(() => {
    if (!req) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // For notify we resolve so the caller proceeds; for confirm
        // we cancel.
        current?.resolve(req.kind === "notify");
        current = null;
        emit();
      } else if (e.key === "Enter") {
        current?.resolve(true);
        current = null;
        emit();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [req]);

  if (!req) return null;

  const confirmLabel = req.confirmLabel ?? (req.kind === "notify" ? t("common.ok") : t("common.confirm"));
  const cancelLabel = req.cancelLabel ?? t("common.cancel");

  const accept = () => {
    current?.resolve(true);
    current = null;
    emit();
  };
  const cancel = () => {
    current?.resolve(false);
    current = null;
    emit();
  };

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          // Backdrop click cancels for confirm, accepts for notify.
          req.kind === "notify" ? accept() : cancel();
        }
      }}
    >
      <div className={`modal-panel${req.variant === "danger" ? " danger" : ""}`}>
        {req.title && <h3 className="modal-title">{req.title}</h3>}
        <div className="modal-message">{req.message}</div>
        <div className="modal-actions">
          {req.kind === "confirm" && (
            <button type="button" onClick={cancel}>{cancelLabel}</button>
          )}
          <button
            type="button"
            className={req.variant === "danger" ? "danger" : "primary"}
            onClick={accept}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
